import React, { useState } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CollectionsIcon from '@mui/icons-material/Collections';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import FavoriteIcon from '@mui/icons-material/Favorite';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { type Property } from '@/api/istaff/property';

const PLACEHOLDER = '/static/images/placeholder.jpg';

type Props = {
  property: Property;
  onClose: () => void;
  onNavigate?: () => void;
  isFavorite?: boolean;
  onFavoriteToggle?: () => void;
};

const PropertyMapTile = ({
  property,
  onClose,
  onNavigate,
  isFavorite = false,
  onFavoriteToggle,
}: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const photos = property.media?.map((m) => m.url).filter(Boolean) ?? [];
  const total = photos.length;
  const [idx, setIdx] = useState(0);

  const prevPhoto = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIdx((i) => (i - 1 + total) % total);
  };
  const nextPhoto = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIdx((i) => (i + 1) % total);
  };

  let leaseSuffix = '';
  if (property.lease_duration === 'season') leaseSuffix = '/ season';
  else if (property.lease_duration === 'year') leaseSuffix = '/ year';

  const currentPhoto = total > 0 ? photos[idx] : PLACEHOLDER;
  const openProperty = () => {
    onNavigate?.();
    navigate(`/property/${property.id}`);
  };
  const handleCardKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      openProperty();
    }
  };

  return (
    <Box data-map-tile sx={{ position: 'relative' }}>
      {/* Mobile X button */}
      <IconButton
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        size="small"
        sx={{
          display: { xs: 'flex', sm: 'none' },
          position: 'absolute',
          top: -14,
          right: -14,
          zIndex: 1,
          bgcolor: '#fff',
          width: 32,
          height: 32,
          boxShadow: '0px 2px 8px rgba(0,0,0,0.15)',
          '&:hover': { bgcolor: '#f5f5f5' },
        }}
      >
        <CloseIcon sx={{ fontSize: 16 }} />
      </IconButton>

      {/* Mobile card */}
      <Box
        role="link"
        tabIndex={0}
        onClick={openProperty}
        onKeyDown={handleCardKeyDown}
        sx={{
          display: { xs: 'flex', sm: 'none' },
          flexDirection: 'column',
          bgcolor: '#fff',
          borderRadius: '8px',
          overflow: 'hidden',
          boxShadow: '0px 8px 48px 0px #00000033',
          cursor: 'pointer',
        }}
      >
        {/* Top row: image 92x130 + content */}
        <Box sx={{ display: 'flex', flexDirection: 'row', p: 1.5, gap: 1.5 }}>
          <Box
            component="img"
            src={currentPhoto}
            alt={property.name ?? t('listing')}
            onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
              e.currentTarget.src = PLACEHOLDER;
            }}
            sx={{
              width: 92,
              height: 130,
              objectFit: 'cover',
              borderRadius: '4px',
              flexShrink: 0,
              display: 'block',
            }}
          />

          <Box sx={{ flex: 1, minWidth: 0, position: 'relative' }}>
            {onFavoriteToggle && (
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  onFavoriteToggle();
                }}
                size="small"
                sx={{
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  p: 0.25,
                  color: isFavorite ? '#FF5A00' : '#898989',
                }}
              >
                {isFavorite ? (
                  <FavoriteIcon sx={{ fontSize: 20 }} />
                ) : (
                  <FavoriteBorderIcon sx={{ fontSize: 20 }} />
                )}
              </IconButton>
            )}

            <Typography
              fontWeight={700}
              fontSize={15}
              lineHeight="20px"
              mb={0.5}
              pr={3.5}
            >
              {property.name}
            </Typography>

            <Typography fontSize={12} color="text.secondary" mb={1}>
              {t('id')}: #{property.id}
            </Typography>

            <Box sx={{ display: 'flex', gap: 2, mb: 1, flexWrap: 'nowrap' }}>
              {property.square_meters != null && (
                <Box sx={{ minWidth: 0 }}>
                  <Typography fontSize={10} color="text.secondary" noWrap>
                    {t('dimensions')}
                  </Typography>

                  <Typography fontSize={12} fontWeight={600} noWrap>
                    {property.square_meters} m²
                  </Typography>
                </Box>
              )}

              {property.number_of_rooms != null && (
                <Box sx={{ minWidth: 0 }}>
                  <Typography fontSize={10} color="text.secondary" noWrap>
                    {t('bedrooms')}
                  </Typography>

                  <Typography fontSize={12} fontWeight={600}>
                    {property.number_of_rooms}
                  </Typography>
                </Box>
              )}

              {property.number_of_bathrooms != null && (
                <Box sx={{ minWidth: 0 }}>
                  <Typography fontSize={10} color="text.secondary" noWrap>
                    {t('bathrooms')}
                  </Typography>

                  <Typography fontSize={12} fontWeight={600}>
                    {property.number_of_bathrooms}
                  </Typography>
                </Box>
              )}
            </Box>

            {property.price != null && (
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                <Typography
                  sx={{ fontFamily: 'Manrope', fontWeight: 700, fontSize: 16 }}
                >
                  €{property.price}
                </Typography>

                {leaseSuffix && (
                  <Typography
                    sx={{
                      fontFamily: 'Manrope',
                      fontWeight: 400,
                      fontSize: 11,
                      color: '#656565',
                    }}
                  >
                    {leaseSuffix}
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        </Box>

        {/* Bottom: photo nav arrows, only when multiple photos */}
        {total > 1 && (
          <Box
            sx={{
              height: 46,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2,
            }}
          >
            <IconButton
              onClick={prevPhoto}
              size="small"
              sx={{
                width: 32,
                height: 32,
                '&:hover': { bgcolor: '#f5f5f5' },
              }}
            >
              <Box
                component="img"
                src="/icons/left_arrow.svg"
                width={16}
                height={16}
              />
            </IconButton>

            <IconButton
              onClick={nextPhoto}
              size="small"
              sx={{
                width: 32,
                height: 32,
                '&:hover': { bgcolor: '#f5f5f5' },
              }}
            >
              <Box
                component="img"
                src="/icons/right_arrow.svg"
                width={16}
                height={16}
              />
            </IconButton>
          </Box>
        )}
      </Box>

      {/* Desktop card */}
      <Box
        role="link"
        tabIndex={0}
        onClick={openProperty}
        onKeyDown={handleCardKeyDown}
        sx={{
          display: { xs: 'none', sm: 'block' },
          width: '100%',
          bgcolor: '#fff',
          borderRadius: '8px',
          overflow: 'hidden',
          boxShadow: '0px 8px 48px 0px #00000033',
          cursor: 'pointer',
        }}
      >
        <Box sx={{ position: 'relative', height: 196, bgcolor: '#f3f4f6' }}>
          <Box
            component="img"
            src={currentPhoto}
            alt={property.name ?? t('listing')}
            onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
              e.currentTarget.src = PLACEHOLDER;
            }}
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: 'block',
            }}
          />

          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            size="small"
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              bgcolor: 'transparent',
              width: 32,
              height: 32,
              color: '#000',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.08)' },
            }}
          >
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>

          {onFavoriteToggle && (
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                onFavoriteToggle();
              }}
              size="small"
              sx={{
                position: 'absolute',
                top: 8,
                right: 48,
                bgcolor: '#fff',
                width: 32,
                height: 32,
                color: isFavorite ? '#FF5A00' : '#000',
                '&:hover': { bgcolor: '#f5f5f5' },
              }}
            >
              {isFavorite ? (
                <FavoriteIcon sx={{ fontSize: 18 }} />
              ) : (
                <FavoriteBorderIcon sx={{ fontSize: 18 }} />
              )}
            </IconButton>
          )}

          {total > 1 && (
            <>
              <IconButton
                onClick={prevPhoto}
                size="small"
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: 8,
                  transform: 'translateY(-50%)',
                  bgcolor: 'rgba(255,255,255,0.9)',
                  width: 32,
                  height: 32,
                  '&:hover': { bgcolor: '#fff' },
                }}
              >
                <ChevronLeftIcon sx={{ fontSize: 20 }} />
              </IconButton>

              <IconButton
                onClick={nextPhoto}
                size="small"
                sx={{
                  position: 'absolute',
                  top: '50%',
                  right: 8,
                  transform: 'translateY(-50%)',
                  bgcolor: 'rgba(255,255,255,0.9)',
                  width: 32,
                  height: 32,
                  '&:hover': { bgcolor: '#fff' },
                }}
              >
                <ChevronRightIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </>
          )}

          {total > 0 && (
            <Box
              sx={{
                position: 'absolute',
                bottom: 10,
                right: 10,
                bgcolor: 'rgba(0,0,0,0.55)',
                color: '#fff',
                borderRadius: 2,
                px: 1,
                py: 0.25,
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                fontSize: 12,
              }}
            >
              <CollectionsIcon sx={{ fontSize: 14 }} />
              {idx + 1}/{total}
            </Box>
          )}
        </Box>

        <Box sx={{ p: 2 }}>
          <Typography fontWeight={700} fontSize={20} lineHeight="28px" mb={0.5}>
            {property.name}
          </Typography>

          <Typography fontSize={14} color="text.secondary" mb={2}>
            {t('id')}: #{property.id}
          </Typography>

          <Box sx={{ display: 'flex', gap: 4, mb: 2 }}>
            {property.square_meters != null && (
              <Box>
                <Typography fontSize={12} color="text.secondary" mb={0.25}>
                  {t('dimensions')}
                </Typography>

                <Typography fontSize={15} fontWeight={500}>
                  {property.square_meters} m²
                </Typography>
              </Box>
            )}

            {property.number_of_rooms != null && (
              <Box>
                <Typography fontSize={12} color="text.secondary" mb={0.25}>
                  {t('bedrooms')}
                </Typography>

                <Typography fontSize={15} fontWeight={500}>
                  {property.number_of_rooms}
                </Typography>
              </Box>
            )}

            {property.number_of_bathrooms != null && (
              <Box>
                <Typography fontSize={12} color="text.secondary" mb={0.25}>
                  {t('bathrooms')}
                </Typography>

                <Typography fontSize={15} fontWeight={500}>
                  {property.number_of_bathrooms}
                </Typography>
              </Box>
            )}
          </Box>

          {property.price != null && (
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
              <Typography
                sx={{
                  fontFamily: 'Manrope',
                  fontWeight: 600,
                  fontSize: 18,
                  lineHeight: '150%',
                  letterSpacing: 0,
                }}
              >
                €{property.price}
              </Typography>

              {leaseSuffix && (
                <Typography
                  sx={{
                    fontFamily: 'Manrope',
                    fontWeight: 400,
                    fontSize: 12,
                    lineHeight: '19px',
                    letterSpacing: 0,
                    color: '#656565',
                  }}
                >
                  {leaseSuffix}
                </Typography>
              )}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default PropertyMapTile;
